import { ReactNode, useState } from 'react';
import { KeyboardArrowDown } from '@mui/icons-material';
import { Box } from '@mui/material';
import { get, useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { HTTP_URL_PATTERN } from '../../../helpers/knowledgeHelper';
import { useTranslation } from '../../../hooks/useTranslation';
import StyledFormItem from '../../../styles/StyledFormItem';
import Input from '../Input/Input';
import Switch from '../Switch/Switch';
import Text from '../Text/Text';
import ToolTip from '../ToolTip/ToolTip';
import WebscrapNumberField from '../WebscrapConfigFields/WebscrapNumberField';

// Same spacing fix as WebscrapConfigFields' FieldsStack: overrides label
// margin-bottom and hides the empty MuiFormHelperText paragraph Input always
// renders, so stacked fields keep one even gap instead of doubled spacing.
const FieldsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  & > label {
    margin-bottom: 0 !important;
  }

  & .MuiFormHelperText-root:empty {
    display: none;
  }
`;

const PaginationToggle = styled.button<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  font-weight: 600;
  color: inherit;
  cursor: pointer;

  svg {
    width: 1.125rem;
    height: 1.125rem;
    transition: transform 0.15s ease;
    transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
  }
`;

const fieldLabel = (text: string, tooltip: string): ReactNode => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
    {text}
    <ToolTip title={tooltip} />
  </Box>
);

interface DigestEntryFieldsProps {
  index: number;
}

// One row of DigestsConfigFields' field array - registers
// webscrap_digests.{index}.* on the surrounding react-hook-form context.
// Field names match the backend's DigestRule (libs/webscrap/config.py)
// directly. Pagination/detail-page fields are grouped behind their own
// toggle since they only matter for a paginated listing or per-item pages,
// mirroring WebscrapConfigFields' advanced-settings toggle.
const DigestEntryFields = ({ index }: DigestEntryFieldsProps) => {
  const { t } = useTranslation();
  const {
    watch,
    formState: { errors },
  } = useFormContext();
  const [showPagination, setShowPagination] = useState(false);
  const fetchDetailPages = watch(
    `webscrap_digests.${index}.fetch_detail_pages`
  );
  const name = (field: string) => `webscrap_digests.${index}.${field}`;
  const fieldError = (field: string) => get(errors, name(field));
  // The scraper needs all four of these (DigestRule.from_dict), so they're
  // required here rather than the row being dropped from the payload.
  const requiredProps = (field: string) => ({
    required: true,
    withError: !!fieldError(field),
    helperText: t('Required for this digest, or remove it'),
  });
  const urlError = fieldError('url');

  return (
    <FieldsStack>
      <StyledFormItem marginBottom="0">
        <Text type="label">
          {fieldLabel(
            t('List heading'),
            t(
              'The title shown above this list in the knowledge base (e.g. "Upcoming events").'
            )
          )}
        </Text>
        <Input
          name={name('heading')}
          placeholder={t('Upcoming events')}
          inputProps={{
            'aria-label': t('List heading {{number}}', {
              number: index + 1,
            }),
          }}
        />
      </StyledFormItem>

      <StyledFormItem marginBottom="0">
        <Text type="label">
          {fieldLabel(
            t('Listing page URL'),
            t(
              'The page listing the items to summarize (e.g. an events or catalogue page).'
            )
          )}
        </Text>
        <Input
          name={name('url')}
          placeholder="https://example.com/events"
          required
          pattern={{ value: HTTP_URL_PATTERN, message: '' }}
          withError={!!urlError}
          helperText={
            urlError?.type === 'pattern'
              ? t('Enter a full URL starting with http:// or https://')
              : t('Required for this digest, or remove it')
          }
          inputProps={{
            'aria-label': t('Listing page URL {{number}}', {
              number: index + 1,
            }),
          }}
        />
      </StyledFormItem>

      <StyledFormItem marginBottom="0">
        <Text type="label">
          {fieldLabel(
            t('Item selector'),
            t(
              'CSS selector matching each item in the list (e.g. one event or product).'
            )
          )}
        </Text>
        <Input
          name={name('item_selector')}
          placeholder="article.event-item"
          {...requiredProps('item_selector')}
          inputProps={{
            'aria-label': t('Item selector {{number}}', {
              number: index + 1,
            }),
          }}
        />
      </StyledFormItem>

      <StyledFormItem marginBottom="0">
        <Text type="label">
          {fieldLabel(
            t('Title selector'),
            t('CSS selector for the title, relative to each item found above.')
          )}
        </Text>
        <Input
          name={name('title_selector')}
          placeholder="h3"
          {...requiredProps('title_selector')}
          inputProps={{
            'aria-label': t('Title selector {{number}}', {
              number: index + 1,
            }),
          }}
        />
      </StyledFormItem>

      <StyledFormItem marginBottom="0">
        <Text type="label">
          {fieldLabel(
            t('Date attribute'),
            t(
              'The HTML attribute on each item holding its date (e.g. data-date).'
            )
          )}
        </Text>
        <Input
          name={name('date_attr')}
          placeholder="data-date"
          {...requiredProps('date_attr')}
          inputProps={{
            'aria-label': t('Date attribute {{number}}', {
              number: index + 1,
            }),
          }}
        />
      </StyledFormItem>

      <Switch
        name={name('drop_past')}
        label={fieldLabel(
          t('Drop past items'),
          t(
            "Remove items whose date has already passed, so the list only shows what's coming up."
          )
        )}
      />

      <WebscrapNumberField
        name={name('max_items')}
        label={t('Max items')}
        tooltip={t(
          'The most items kept in this list, in date order. 0 for no limit.'
        )}
      />

      <PaginationToggle
        type="button"
        $open={showPagination}
        aria-expanded={showPagination}
        aria-controls={`digest-pagination-${index}`}
        onClick={() => setShowPagination(open => !open)}
      >
        {showPagination
          ? t('Hide pagination & detail pages')
          : t('Show pagination & detail pages')}
        <KeyboardArrowDown aria-hidden="true" />
      </PaginationToggle>

      {showPagination && (
        <FieldsStack id={`digest-pagination-${index}`}>
          <StyledFormItem marginBottom="0">
            <Text type="label">
              {fieldLabel(
                t('Page URL template'),
                t(
                  'URL pattern for additional listing pages, with {page} as a placeholder for the page number (e.g. https://example.com/events/page/{page}/).'
                )
              )}
            </Text>
            <Input
              name={name('page_url_template')}
              placeholder="https://example.com/events/page/{page}/"
              inputProps={{
                'aria-label': t('Page URL template {{number}}', {
                  number: index + 1,
                }),
              }}
            />
          </StyledFormItem>

          <WebscrapNumberField
            name={name('max_pages')}
            label={t('Max pages')}
            tooltip={t(
              'The most pages this digest fetches in one run - shared between listing pages and, if detail pages are enabled, the pages for each item.'
            )}
          />

          <Switch
            name={name('fetch_detail_pages')}
            label={fieldLabel(
              t('Fetch detail pages'),
              t(
                "Also fetch each item's own page and include its content alongside the listing details."
              )
            )}
          />

          {fetchDetailPages && (
            <StyledFormItem marginBottom="0">
              <Text type="label">
                {fieldLabel(
                  t('Detail link selector'),
                  t(
                    "CSS selector (optionally with @attr, e.g. a.event-link@href) for the link to each item's own page."
                  )
                )}
              </Text>
              <Input
                name={name('detail_link_selector')}
                placeholder="a.event-link@href"
                inputProps={{
                  'aria-label': t('Detail link selector {{number}}', {
                    number: index + 1,
                  }),
                }}
              />
            </StyledFormItem>
          )}
        </FieldsStack>
      )}
    </FieldsStack>
  );
};

export default DigestEntryFields;
